import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  useToast 
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from '../../../util/helper';
import { getPersonalityAliasSuggestions, getPersonalityDetailsByAlias } from '../../../services/Personality';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { getThemeTeamData, updateThemeTeamData } from '../../../services/Theme';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const Teams = () => {
  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();

  const schema =  {
    "title": "Theme Teams Fields",
  "type": "object",
  "properties": {
    "editors": {
      "title": "Editors",
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
          "image_alias": { "type": "string" },
          "full_name": { "type": "string" },
        }
      }
    },
    "publishers": {
      "title": "Publishers",
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
          "image_alias": { "type": "string" },
          "full_name": { "type": "string" },
        }
      }
    },
  }
  }
  
  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "editors": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
        full_name: {
          "ui:readonly": true,
        },
      },
    },
    "publishers": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
        full_name: {
          "ui:readonly": true,
        },
      },
    },
    
    };



  // State to track form data with calculated age
  const [formData, setFormData] = useState({});
  const [formKey, setFormKey] = useState(0);
  const formRef = useRef(null);

  // Function to identify changed items for array fields
  const getChangedItems = (currentData, originalData, fieldName) => {
    const currentArray = currentData[fieldName] || [];
    const originalArray = originalData[fieldName] || [];
    
    const changedItems = [];
    const processedOriginalAliases = new Set();

    // Create a map of original items by alias for efficient lookup
    const originalMap = new Map();
    originalArray.forEach(item => {
      if (item.alias) {
        originalMap.set(item.alias, item);
      }
    });

    // Check current items against original
    currentArray.forEach(currentItem => {
      if (currentItem.alias && originalMap.has(currentItem.alias)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.alias);
        
        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });
        
        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }
        
        processedOriginalAliases.add(currentItem.alias);
      } else {
        // No alias or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalArray.forEach(originalItem => {
      if (originalItem.alias && !processedOriginalAliases.has(originalItem.alias)) {
        // This item doesn't exist in current data - it's deleted
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });
    
    return changedItems;
  };

  // Update form data when processedData changes
  useEffect(() => {
    if (processedData && Object.keys(processedData).length > 0) {
      setFormData({
        ...processedData,
      });
    }
  }, [processedData]);

  // Handle form changes and recalculate age
  const handleFormChange = (formData) => {
    setFormData({
      ...formData.formData,
    });
  };



  const fetchPersonalityAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadPersonalityAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadPersonalityAliasSuggestions = async (inputValue) => {
    try {
      const result = await getPersonalityAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };

  // Function to handle auto-fill when personality is selected
  const handlePersonalitySelection = async (selectedOption, fieldPath) => {
    if (!selectedOption || !selectedOption.value) return;
    
    try {
      const personalityData = await getPersonalityDetailsByAlias(selectedOption.value);
      if (personalityData) {
        // Extract the field path components (e.g., "root_editors_0" -> ["editors", "0"])
        const pathParts = fieldPath.split('_');
        const fieldName = pathParts[1]; // editors or publishers
        const itemIndex = parseInt(pathParts[2]); // 0, 1, 2, etc.
        
        // Update the form data with the auto-filled values using functional update
        setFormData(prevData => {
          const newData = { ...prevData };
          if (!newData[fieldName]) {
            newData[fieldName] = [];
          }
          if (!newData[fieldName][itemIndex]) {
            newData[fieldName][itemIndex] = {};
          }
          
          // Auto-fill the fields
          newData[fieldName][itemIndex] = {
            ...newData[fieldName][itemIndex],
            full_name: personalityData?.full_name || '',
            alias: selectedOption.value,
          };
          
          return newData;
        });
        
        // Force form re-render after a small delay to ensure state is updated
        setTimeout(() => {
          setFormKey(prev => prev + 1);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching personality details:', error);
            showErrorToast(toast, "Failed to fetch personality details");
    }
  };

  const widgets = {
    personalityAliasTypeahead: (props) => {
      // Extract field path from the props
      const fieldPath = props?.id || '';
      
      return (
        <TypeaheadWidget
          {...props}
          fetchOptions={fetchPersonalityAliasSuggestions}
          onSelectionChange={(selectedOption) => handlePersonalitySelection(selectedOption, fieldPath)}
        />
      );
    },
  };


  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getThemeTeamData(alias);
          if (result) {

            const fieldsToTransform = [
              'poster_image_carousel_alias', 
              'other_publicity_image_carousel_alias'
            ];
            const transformedData = transformData(result, fieldsToTransform);
            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);
        
        // Get changed items for each array field
        const arrayFields = ['editors', 'publishers'];
        const changedData = {};
        const deletedData = {};
        let hasChanges = false;
        
        // Check for changes in array fields
        arrayFields.forEach(fieldName => {
          const changedItems = getChangedItems(formData, originalData, fieldName);
          
          // Separate added/updated items from deleted items
          const addsAndUpdates = changedItems.filter(item => item.type === 'add' || item.type === 'update');
          const deletes = changedItems.filter(item => item.type === 'delete');
          
          if (addsAndUpdates.length > 0) {
            // Keep the 'type' property in the payload
            changedData[fieldName] = addsAndUpdates;
            hasChanges = true;
          }
          
          if (deletes.length > 0) {
            // Keep the 'type' property in the payload
            deletedData[fieldName] = deletes;
            hasChanges = true;
          }
        });
        
        if (hasChanges) {
          const fieldsToTransform = [
            'poster_image_carousel_alias', 
            'other_publicity_image_carousel_alias'
          ];
          const updatedFormData = reverseTransformData(changedData, fieldsToTransform);
          
          const result = await updateThemeTeamData(alias, updatedFormData, deletedData);

          if (result?.status === 200) {
            // Update originalData to reflect the new baseline after successful save
            const updatedOriginalData = { ...originalData };
            arrayFields.forEach(fieldName => {
              const currentArray = formData[fieldName] || [];
              updatedOriginalData[fieldName] = [...currentArray];
            });
            setOriginalData(updatedOriginalData);
            
            setQuickSave(prev => !prev);
            setProcessedData(formData); // Update processed data to reflect current form state
                        showSuccessToast(toast, "Teams data updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating teams data:', error);
            showErrorToast(toast, "Failed to update data");
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form 
          ref={formRef}
          key={formKey}
          schema={schema} 
          uiSchema={uiSchema} 
          formData={formData} 
          validator={validator} 
          onSubmit={(e) => handleSubmit(e.formData)} 
          onChange={handleFormChange}
          widgets={widgets} 
        />
      )}
    </Box>
  );
};

export default Teams;